/**
 * Der Gruss im Spiegel richtet sich nach der Uhr des Hoerers. (16.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit dem Spiegel vor sich:
 *
 *   *„Die Frage, ob sich ‚guten Morgen' auf das gestern bezieht oder auf das,
 *   was er in der Frueh hoert. Ich denke, du kannst einfach sagen: ‚Hallo
 *   Albert' oder ‚Hallo Martin', weil man weiss, ob man sich den Spiegel
 *   ueberhaupt am Vormittag anhoert. Oder man macht es dynamisch, sodass man
 *   sagt: ‚Guten Morgen', ‚Hallo Martin', oder ‚Guten Abend', je nachdem,
 *   wann man den Spiegel sich anhoert, sodass der Text dynamisch auf die
 *   Uhrzeit bezogen ist."*
 *
 * Der Spiegel entsteht in der Nacht und wird am Morgen gehoert — meistens.
 * Wer ihn abends nachholt, bekam trotzdem „Guten Morgen": ein Gruss, der aus
 * dem Entstehungsmoment sprach statt aus dem Moment des Hoerens.
 *
 * ## Warum das beim LESEN passiert und nicht beim Schreiben
 *
 * ⚠️ Der gespeicherte Text bleibt, wie er erzeugt wurde. Getauscht wird erst,
 *    wenn jemand ihn abruft — sonst muesste man den Spiegel neu schreiben,
 *    sobald sich die Tageszeit aendert, und das waere ein Lauf des
 *    staerksten Modells fuer ein einziges Wort.
 *
 * ⚠️ Nur der Gruss, nur in den ersten Zeilen. Steht weiter unten im Text
 *    „an jenem Morgen", bleibt das stehen — es ist eine Erinnerung an
 *    gestern, keine Anrede.
 */

/** Wie man um diese Stunde grüßt. */
export function tagesgruss(stunde: number): string {
  if (stunde >= 5 && stunde < 11) return "Guten Morgen";
  if (stunde >= 11 && stunde < 17) return "Hallo";
  if (stunde >= 17 && stunde < 22) return "Guten Abend";
  // Zwischen 22 und 5 passt kein Tagesgruss mehr — und „Gute Nacht" klingt
  // wie ein Abschied, wo einer gerade anfängt zu lesen.
  return "Hallo";
}

/** Die Grussformeln, die ein Modell an den Anfang setzen kann. */
const GRUSSFORMELN =
  /^(Guten Morgen|Guten Tag|Guten Abend|Gute Nacht|Schönen guten Morgen|Hallo|Servus|Hi|Hey)\b/;

/**
 * Tauscht den Gruss in den ersten Zeilen gegen den zur Stunde passenden.
 *
 * Findet sich keiner, bleibt der Text unverändert — es wird nie einer
 * hinzuerfunden. Überschriften werden übersprungen, sie sind keine Anrede.
 */
export function grussAnpassen(text: string, stunde: number): string {
  const zeilen = (text || "").split("\n");
  const passend = tagesgruss(stunde);
  // Nur die ersten paar Zeilen ansehen: die Anrede steht am Anfang oder
  // nirgends.
  const fenster = Math.min(zeilen.length, 8);
  for (let i = 0; i < fenster; i++) {
    const zeile = zeilen[i] ?? "";
    const roh = zeile.trim();
    if (!roh) continue;
    if (roh.startsWith("#")) continue; // Überschrift, keine Anrede
    const treffer = GRUSSFORMELN.exec(roh);
    if (!treffer) {
      // Die erste echte Textzeile hat keinen Gruss — dann gibt es keinen.
      return text;
    }
    if (treffer[1] === passend) return text;
    zeilen[i] = zeile.replace(treffer[1]!, passend);
    return zeilen.join("\n");
  }
  return text;
}

/** Die Stunde in Wiener Zeit — dieselbe Uhr wie der Nachtlauf. */
export function wienerStunde(jetzt: Date): number {
  const h = parseInt(
    jetzt.toLocaleString("en-GB", {
      timeZone: "Europe/Vienna",
      hour: "2-digit",
      hour12: false,
    }),
    10,
  );
  return Number.isNaN(h) ? 9 : h;
}
